import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Toolbar, Tabs, Tab, Grid } from "@material-ui/core";
import { useRouteMatch } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Helmet } from "react-helmet";
import _, { debounce } from "lodash";
import { LinearProgress, Pill, Text, Button } from "../../components";
import Editor from "@monaco-editor/react";
import { configureMonaco } from "../../schema/task";
import { NEW_TASK_TEMPLATE, taskFields } from "../../schema/task";
import ResetConfirmationDialog from "./ResetConfirmationDialog";
import SaveTaskDialog from "./SaveTaskDialog";
import { useTask } from "../../data/task";
import { usePushHistory } from "../../components/NavLink";
import { colors } from "../../theme/variables";
import TaskDefTasks from "./TaskDefTasks";
import { ArrowBack, Refresh } from "@material-ui/icons";
import { IconButton } from "@material-ui/core";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    height: "100%",
    alignItems: "stretch",
    flexDirection: "column",
  },
  name: {
    fontWeight: "bold",
  },
  rightButtons: {
    display: "flex",
    flexGrow: 1,
    justifyContent: "flex-end",
    gap: 8,

    "&>button": {
      margin: "0.5rem 0",
      padding: "4px 0",
      fontSize: "0.75rem",
      fontFamily: "Poppins, regular",
      lineHeight: "1.5",
    },
  },
  workflowDefTabs: {
    // marginBottom: "1rem",
  },
  workflowDefTabPannels: {
    height: "calc(100vh - 9rem)", //subtracted hieght for tab header and toolbar
    overflow: "auto",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    // backgroundColor: "#fff",
    // height: "100vh",
  },
  fields: {
    width: "100%",
    height: "100%",

    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    gap: 15,
  },
});

export default function TaskDefinition(props) {
  const permissions = props?.permissions;
  const classes = useStyles();
  const match = useRouteMatch();
  const navigate = usePushHistory();

  const [isModified, setIsModified] = useState(false);
  const [jsonErrors, setJsonErrors] = useState([]);
  const [resetDialog, setResetDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const editorRef = useRef();
  const taskName = _.get(match, "params.name");
  const debouncedHandleChange = useRef(null);

  const { data: taskDef, isFetching, refetch } = useTask(
    taskName,
    NEW_TASK_TEMPLATE
  );

  const [data, setData] = useState(taskDef ? taskDef : {});
  const [newData, setNewData] = useState(taskDef ? taskDef : {});

  const inputFields = taskFields;

  const taskJson = useMemo(() => {
    let sorted_obj = data
      ? Object.keys(data)
          .sort()
          .reduce((temp_obj, key) => {
            temp_obj[key] = data[key];
            return temp_obj;
          }, {})
      : {};
    return data ? JSON.stringify(sorted_obj, null, 2) : "";
  }, [data]);

  const { data: originalData } = useTask(taskName, NEW_TASK_TEMPLATE);

  const originalTaskJson = useMemo(() => {
    let sorted_obj = originalData
      ? Object.keys(originalData)
          .sort()
          .reduce((temp_obj, key) => {
            temp_obj[key] = originalData[key];
            return temp_obj;
          }, {})
      : "";
    return originalData ? JSON.stringify(sorted_obj, null, 2) : "";
  }, [originalData]);

  function handleTasks(val) {
    if (!_.isEmpty(data) && JSON.stringify(val) !== JSON.stringify(data)) {
      setData(val);
      setIsModified(JSON.stringify(originalData) !== JSON.stringify(data));
    }
  }

  useEffect(() => {
    if (taskDef) {
      setData(taskDef);
    }
  }, [JSON.stringify(taskDef)]);

  // Save
  const handleOpenSave = () => {
    setSaveDialog({
      original: taskName ? originalTaskJson : "",
      originalObj: taskName ? originalData : null,
      modified: taskJson || null,
    });
  };

  const handleSaveSuccess = (name) => {
    setSaveDialog(null);
    setIsModified(false);

    if (name === taskName) {
      refetch();
    } else {
      navigate(`/taskDef/${name}`);
    }
  };

  // Reset
  const doReset = () => {
    if (!editorRef?.current?.getModel) return;
    refetch();
    setData(taskDef);
    setResetDialog(false);
    setIsModified(false);
  };

  // Monaco Handlers
  const handleEditorWillMount = (monaco) => {
    configureMonaco(monaco);
  };

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
  };

  const handleValidate = (markers) => {
    setJsonErrors(markers);
  };

  const handleEditorChange = useCallback((value, event) => {
    if (value === "") {
      handleChange(value);
      return;
    }

    if (debouncedHandleChange.current) {
      debouncedHandleChange.current.cancel();
    }

    debouncedHandleChange.current = debounce((value) => {
      handleChange(value);
    }, 5000);

    debouncedHandleChange.current(value);
  }, []);

  useEffect(() => {
    let timeout;
    timeout = setTimeout(() => {
      setData(newData);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [newData]);

  const handleChange = (v) => {
    // setNewData(JSON.parse(v ? v : "{}"));

    try {
      var parsedNewWorkflow = JSON.parse(v);
      if (parsedNewWorkflow) {
        setNewData(parsedNewWorkflow);
        setIsModified(v !== taskJson);
      }
    } catch (e) {
      return console.error(e);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    if (newValue !== 1 && editorRef?.current?.getModel()) {
      if (debouncedHandleChange.current) debouncedHandleChange.current.cancel();
    }
  };

  return (
    <>
      <Helmet>
        <title>Conductor UI - Task Definition - {taskName || "New Task"}</title>
      </Helmet>

      <SaveTaskDialog
        document={saveDialog}
        onCancel={() => setSaveDialog(null)}
        onSuccess={handleSaveSuccess}
      />

      <ResetConfirmationDialog
        version={resetDialog}
        onConfirm={doReset}
        onClose={() => setResetDialog(false)}
      />

      {isFetching && <LinearProgress />}
      <div className={classes.wrapper}>
        <Toolbar>
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/taskDefs");
            }}>
            <ArrowBack />
          </IconButton>
          <Text className={classes.name}>{taskName || "NEW"}</Text>

          {isModified ? (
            <Pill color="yellow" label="Modified" />
          ) : (
            <Pill label="Unmodified" />
          )}
          {!_.isEmpty(jsonErrors) && <Pill color="red" label="Validation" />}

          {permissions?.tasks?.actions?.create_task === "True" && (
            <div className={classes.rightButtons}>
              <Button
                disabled={!_.isEmpty(jsonErrors) || !isModified}
                onClick={handleOpenSave}
                variant="contained"
                color="primary">
                Save
              </Button>
              <Button
                onClick={() => setResetDialog(true)}
                variant="outlined"
                color="primary"
                disabled={!isModified}>
                Reset
              </Button>
            </div>
          )}
        </Toolbar>
        <div className={classes.workflowDefTabs}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="basic tabs">
            <Tab label="Task" />
            <Tab label="Code" />
          </Tabs>
        </div>
        <div
          className={classes.workflowDefTabPannels}
          hidden={tabIndex !== 0}
          value={tabIndex}
          index={1}>
          <TaskDefTasks
            inputFields={inputFields}
            data={data}
            setData={setData}
            handleTasks={handleTasks}
            jsonErrors={jsonErrors}
            setJsonErrors={setJsonErrors}></TaskDefTasks>
        </div>
        <div
          className={classes.workflowDefTabPannels}
          hidden={tabIndex !== 1}
          value={tabIndex}
          index={0}>
          <Editor
            height="100%"
            width="100%"
            theme="vs-light"
            language="json"
            value={taskJson}
            autoIndent={true}
            beforeMount={handleEditorWillMount}
            onMount={handleEditorDidMount}
            onValidate={handleValidate}
            // onChange={handleChange}
            onChange={(newValue, event) => handleEditorChange(newValue, event)}
            options={{
              smoothScrolling: true,
              selectOnLineNumbers: true,
              minimap: {
                enabled: false,
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
